import { MAX_REPEAT_EVERY, REPEAT_FROM, REPEAT_UNITS, RepeatFrom, RepeatUnit, TodoData, TodoRepeat, isRepeatDayFor } from "../../types";
import { addDaysLocal, ymdOf } from "./dueDate";

// Client-side reset of repeating todos. In "done" mode (the default) a done
// todo with a `repeat` comes back unchecked once `every` units have passed
// since the local calendar day it was done (doneAt). In "start" mode it comes
// due `every` units after `anchor` whether or not it was checked, and the
// reset moves `anchor` forward to the latest due day. Everything here is in the client's local time and
// works on YYYY-MM-DD strings, so a day never shifts across a zone or a DST
// change. There is no server job: DataProvider runs this once after the
// first successful load (see dueRepeatResets).

// Most todos one load will uncheck. Anything past this waits for the next
// load, so a long absence can't turn into an unbounded burst of PATCHes.
export const MAX_REPEAT_RESETS_PER_LOAD = 50;
// How many of those PATCHes are in flight at once, so a long absence comes
// back as a short trickle rather than one burst against the API.
export const REPEAT_RESET_CONCURRENCY = 5;

// Runs `fn` over `items` with at most `limit` calls in flight, in list order.
// Each item is tried once: a rejection is swallowed here (callers log their
// own) so one failure never stops the rest. `stop` is checked before each
// item starts. Resolves once every started call has settled.
export const runLimited = async <T>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<unknown>,
  stop: () => boolean = () => false,
) => {
  let next = 0;
  const worker = async () => {
    while (next < items.length && !stop()) {
      const item = items[next++];
      try {
        await fn(item);
      } catch {
        // Already logged by fn; keep going.
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(Math.max(1, limit), items.length) }, worker));
};

// Structural check, mirroring the API's validator (site-backend/utils/todo.ts):
// a stored todo should already be valid, but the reset must never throw or
// fire on a malformed field.
const isRepeat = (v: unknown): v is TodoRepeat => {
  if (typeof v !== "object" || v === null) return false;
  const { every, unit, from, anchor, day } = v as { every?: unknown; unit?: unknown; from?: unknown; anchor?: unknown; day?: unknown };
  return (
    Number.isInteger(every) &&
    (every as number) >= 1 &&
    (every as number) <= MAX_REPEAT_EVERY &&
    (REPEAT_UNITS as readonly unknown[]).includes(unit) &&
    (from === undefined || (REPEAT_FROM as readonly unknown[]).includes(from)) &&
    (anchor === undefined || isYmd(anchor)) &&
    (from !== "start" || anchor !== undefined) &&
    (day === undefined || (anchor !== undefined && isRepeatDayFor(day, anchor as string)))
  );
};

// A real YYYY-MM-DD calendar day, the same check as the API's isCalendarDate.
const isYmd = (v: unknown): v is string => {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};

// Calendar days from `a` to `b` (YYYY-MM-DD). UTC is only used as a day
// counter here, so a DST change can't make a day 23 or 25 hours long.
const daysBetween = (a: string, b: string) => {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
};

const isLeapYear = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
// `m` is 1..12.
const daysInMonth = (y: number, m: number) =>
  m === 2 ? (isLeapYear(y) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31;

// `n` months after a YYYY-MM-DD day, the day clamped to the target month's
// length (Jan 31 + 1 month is Feb 28/29; + 2 months is Mar 31, not a chain of
// clamps). Pure integer arithmetic: Date's own month rollover would turn
// Jan 31 + 1 into Mar 3. `day` lands on that day of month instead of `ymd`'s
// own (Feb 28 + 1 month with day 31 is Mar 31).
export const addMonthsClamped = (ymd: string, n: number, day?: number) => {
  const [y, m, ymdDay] = ymd.split("-").map(Number);
  const d = day ?? ymdDay;
  const months = y * 12 + (m - 1) + n;
  const ty = Math.floor(months / 12);
  const tm = (months % 12) + 1;
  const td = Math.min(d, daysInMonth(ty, tm));
  return `${ty}-${String(tm).padStart(2, "0")}-${String(td).padStart(2, "0")}`;
};

// The local day a repeat next comes due, counted from the day it was done.
export const nextRepeatDay = (doneDay: string, repeat: TodoRepeat) =>
  repeat.unit === "month"
    ? addMonthsClamped(doneDay, repeat.every)
    : addDaysLocal(doneDay, repeat.every * (repeat.unit === "week" ? 7 : 1));

// The latest day on or before `today` that is `anchor` plus a whole number of
// intervals (at least one), or undefined when none has come yet. One step,
// never a loop, so coming back after many cycles moves the anchor once.
// Months count from the stored anchor and clamp (Jan 31 + 1 month is Feb 28),
// landing on `repeat.day` when set (so Feb 28 with day 31 is next due Mar 31)
// and on the anchor's own day otherwise.
export const latestRepeatDay = (anchor: string, repeat: TodoRepeat, today: string) => {
  if (anchor >= today) return undefined;
  if (repeat.unit === "month") {
    const [ay, am, anchorDay] = anchor.split("-").map(Number);
    const [ty, tm, td] = today.split("-").map(Number);
    const ad = repeat.day ?? anchorDay;
    // Whole months from anchor to today, minus one if today's day of month
    // hasn't reached the target day yet; clamping can only make the candidate
    // earlier, so this never overshoots today.
    const months = (ty - ay) * 12 + (tm - am) - (td < ad ? 1 : 0);
    let cycles = Math.floor(months / repeat.every);
    // A clamped month end (anchor Jan 31, today Feb 28) is still due.
    if (addMonthsClamped(anchor, (cycles + 1) * repeat.every, ad) <= today) cycles++;
    return cycles >= 1 ? addMonthsClamped(anchor, cycles * repeat.every, ad) : undefined;
  }
  const step = repeat.every * (repeat.unit === "week" ? 7 : 1);
  const cycles = Math.floor(daysBetween(anchor, today) / step);
  return cycles >= 1 ? addDaysLocal(anchor, cycles * step) : undefined;
};

// True when `todo` should come back unchecked: it is done, repeats, and the
// local day it was done plus the interval is today or earlier. doneAt is left
// in place when a todo is unchecked (see types/todo.ts), so `done` is the
// gate that keeps a reset from re-triggering on the next load. A missing or
// unparseable doneAt, an invalid `now`, or a doneAt after `now` (clock skew)
// is never due.
//
// In "start" mode `done` doesn't gate it: the todo is due once the next day
// after `anchor` has come, and the reset moves `anchor` forward, which is
// what keeps the next load from firing again. An anchor in the future is
// never due.
export const isRepeatDue = (
  todo: Pick<TodoData, "done" | "doneAt" | "repeat">,
  now: Date,
) => {
  if (!isRepeat(todo.repeat)) return false;
  if (todo.repeat.from === "start") {
    if (Number.isNaN(now.getTime())) return false;
    return latestRepeatDay(todo.repeat.anchor!, todo.repeat, ymdOf(now)) !== undefined;
  }
  if (todo.done !== true || typeof todo.doneAt !== "string") return false;
  const doneAt = new Date(todo.doneAt);
  if (Number.isNaN(doneAt.getTime()) || Number.isNaN(now.getTime())) return false;
  const today = ymdOf(now);
  const doneDay = ymdOf(doneAt);
  if (doneDay > today) return false;
  return nextRepeatDay(doneDay, todo.repeat) <= today;
};

// The write that resets a due todo: uncheck it, and in "start" mode also move
// the anchor to the latest due day (a todo that was never checked keeps its
// `done` untouched). A monthly one saved before `day` existed gets its
// anchor's day as `day` first, so moving it to a short month's end doesn't
// lose the 31st. Only call this for a todo isRepeatDue says is due.
export const repeatResetUpdates = (
  todo: Pick<TodoData, "done" | "repeat">,
  now: Date,
): { done?: false; repeat?: TodoRepeat } => {
  const repeat = todo.repeat;
  if (repeat?.from !== "start" || !repeat.anchor) return { done: false };
  const withDay =
    repeat.unit === "month" && repeat.day === undefined ? { ...repeat, day: dayOfMonth(repeat.anchor) } : repeat;
  const anchor = latestRepeatDay(withDay.anchor!, withDay, ymdOf(now)) ?? withDay.anchor!;
  return { ...(todo.done && { done: false as const }), repeat: { ...withDay, anchor } };
};

// The todos one load should uncheck: the owner's own (a shared project's
// steps belong to its owner, so a sharee's view never resets anything), due,
// in list order, capped at MAX_REPEAT_RESETS_PER_LOAD.
export const dueRepeatResets = (todos: TodoData[], now: Date, ownerId: string) => {
  const due: TodoData[] = [];
  for (const todo of todos) {
    if (due.length >= MAX_REPEAT_RESETS_PER_LOAD) break;
    if (todo.userId === ownerId && isRepeatDue(todo, now)) due.push(todo);
  }
  return due;
};

const UNIT_SHORT: Record<RepeatUnit, string> = { day: "d", week: "w", month: "mo" };

// The row badge: "↻ 2d", "↻ 1w", "↻ 3mo".
export const formatRepeatShort = (repeat: TodoRepeat) =>
  `↻ ${repeat.every}${UNIT_SHORT[repeat.unit]}`;

// The badge's spoken/tooltip form: "Repeats every day", "Repeats every 2 weeks".
export const describeRepeat = (repeat: TodoRepeat) =>
  repeat.every === 1
    ? `Repeats every ${repeat.unit}`
    : `Repeats every ${repeat.every} ${repeat.unit}s`;

// A repeat with no `from` counts from when it was done.
export const repeatFrom = (repeat: TodoRepeat | undefined): RepeatFrom => repeat?.from ?? "done";

const dayOfMonth = (ymd: string) => Number(ymd.slice(8, 10));

// Reads the menu's Repeat draft: an empty unit is Off (null, which clears
// the field on save), otherwise `every` must be a whole number 1..365, and
// "start" mode needs a real start date (the anchor). "done" mode stays
// exactly { every, unit }, as before there was a `from`. A start-mode
// monthly draft also carries `day`: `stored`'s when the date is still the
// stored anchor (a Feb 28 anchor that means the 31st stays the 31st), the
// picked date's day otherwise.
// undefined means the draft isn't valid yet and there is nothing to save.
export const parseRepeatDraft = (
  everyText: string,
  unit: RepeatUnit | "",
  from: RepeatFrom = "done",
  startDate = "",
  stored?: TodoRepeat,
): TodoRepeat | null | undefined => {
  if (unit === "") return null;
  const trimmed = everyText.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const keepDay = stored?.from === "start" && stored.unit === "month" && stored.anchor === startDate;
  const repeat: TodoRepeat =
    from === "start"
      ? {
          every: Number(trimmed),
          unit,
          from,
          anchor: startDate,
          ...(unit === "month" && { day: (keepDay && stored.day) || dayOfMonth(startDate) }),
        }
      : { every: Number(trimmed), unit };
  return isRepeat(repeat) ? repeat : undefined;
};

// Whether saving `next` would change what's stored (absent and null are both
// Off; an absent `from` is "done", where the anchor doesn't count). `day`
// isn't compared: a draft only differs from the stored one in `day` when the
// anchor differs too (see parseRepeatDraft), or when filling in a missing
// `day`, which the next reset does anyway.
export const sameRepeat = (stored: TodoRepeat | undefined, next: TodoRepeat | null) => {
  if (!stored || !next) return !stored && !next;
  const from = repeatFrom(stored);
  return (
    stored.every === next.every &&
    stored.unit === next.unit &&
    from === repeatFrom(next) &&
    (from !== "start" || stored.anchor === next.anchor)
  );
};
